import React, { useEffect, useState } from 'react';

import useApi from '@/hooks/useApi';
import { useDebounce } from '@/hooks/useDebounce';

import { SmSlider } from '@/components/global/SmSlider';
import { ISlider } from '@/components/global/SmSlider/types';

import {
  TRecommendation,
  TCalculator,
} from '@/services/requests/Recommendation/types/template5';
import { dispatchInteraction } from '@/services/requests/Interactions/helpers';
import { TInteractionClickComponent } from '@/services/requests/Interactions/types';

import { numberToBrl } from '@/helpers/StringHelper/numberToBrl';

type TProps = Pick<TRecommendation, 'id'> & {
  component: TCalculator;
};

export const Calculator = ({ id, component }: TProps) => {
  const {
    title,
    description,
    text1_prefix,
    text1_suffix,
    text2_prefix,
    text2_suffix,
    text3_prefix,
    text3_suffix,
    value: valueString,
    currency_rate,
    conversion_factor,
    min_value,
    max_value,
  } = component;

  const value = parseFloat(valueString.replace(/\./g, ''));
  const currencyRate = Math.round(parseFloat(currency_rate) * 100) / 100;
  const minValue = parseFloat(min_value);
  const maxValue = parseFloat(max_value);
  const conversionFactor = parseFloat(conversion_factor);

  const { Api } = useApi();
  const [inputValue, setInputValue] = useState<number>(value);
  const debouncedValue = useDebounce<number>(inputValue, 1000);

  const handleSliderChange: ISlider['onChange'] = async newValue => {
    setInputValue(newValue.curMax);
  };

  useEffect(() => {
    if (debouncedValue && debouncedValue !== value) {
      const interactionData = {
        recommendationId: id,
        details: {
          currency_rate: currencyRate.toString(),
          miles_value: (
            (debouncedValue / currencyRate) *
            conversionFactor
          ).toFixed(0),
          money_value: debouncedValue.toString(),
        },
      };

      dispatchInteraction<TInteractionClickComponent>(
        Api,
        'click-component',
        interactionData,
      );
    }
  }, [Api, conversionFactor, currencyRate, debouncedValue, id, value]);

  return (
    <>
      <div className="vitrine-template5__calculator">
        <span className="vitrine-template5__calculator__title">{title}</span>
        <span className="vitrine-template5__calculator__description">
          {description}
        </span>
      </div>
      <div className="vitrine-template5__component o-radius-sm o-border-brand-primary-500">
        <span className="vitrine-template5__component__text1">
          {text1_prefix + numberToBrl(inputValue) + text1_suffix}
        </span>
        <SmSlider
          variant="single"
          min={minValue}
          max={maxValue}
          defaultValue={inputValue}
          onChange={handleSliderChange}
        />
        <span className="vitrine-template5__component__min-max">
          <span>{numberToBrl(minValue)}</span>
          <span>{numberToBrl(maxValue)}</span>
        </span>
        <span className="vitrine-template5__component__text2">
          {`${text2_prefix} ${(
            (inputValue / currencyRate) *
            conversionFactor
          ).toFixed(0)} ${text2_suffix}`}
        </span>
        <span className="vitrine-template5__component__text3">{`${text3_prefix} ${numberToBrl(
          currencyRate,
        )} ${text3_suffix}`}</span>
      </div>
    </>
  );
};
